<script setup lang="ts">
import Translator from "@/utils/common/Translator";
import {computed, onBeforeMount, ref, watch} from "vue";
import {
  useDarkMode,
  useDensityMode,
  useHorizontalMode,
  useNamaFlagsStore,
  useQuadLayout,
  useSidebarMenuIconColors,
  useToolbarLabels
} from "@/stores/nama-flags-store.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import PageTitle from "@/components/settings/PageTitle.vue";
import SettingsRow from "@/components/settings/SettingsRow.vue";
import ThemeCard from "@/components/settings/ThemeCard.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import AddRecordIcon from "@/assets/editViewIcons/AddRecordIcon.vue";
import {QSelectOption, Screen} from "quasar";
import {QuestionFieldsWithValues, UserMenuItem} from "@/utils/presenters/interfaces.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import ThemeUtils from "@/utils/common/ThemeUtils.ts";
import DTOVueGUITheme = namaTypes.DTOVueGUITheme;
import QuestionField = namaTypes.QuestionField;

const {darkMode} = useDarkMode();
const {quadLayout} = useQuadLayout();
const {densityMode} = useDensityMode();
const {showToolbarLabels} = useToolbarLabels()
const {horizontalMode} = useHorizontalMode();
const {showSidebarMenuIconColors} = useSidebarMenuIconColors();
const isUltra = computed(() => densityMode.value === 'Ultra');
const themeList = ref<DTOVueGUITheme[]>([]);
const currentTheme = ref(localStorage.getItem('theme-code'));

const themeMenu = computed<UserMenuItem[]>(() => {
  if (!themeList.value)
    return [];
  let menu: UserMenuItem[] = []
  for (let theme of themeList.value)
    menu.push({
      theme: theme,
      handler: () => applyTheme(theme.code)
    })
  return menu;
});


let samplePresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
let sampleQuestionFields: QuestionField[] = QuestionFieldsPresenter.createSampleQuestionFields()
let soundPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
let soundQuestionFieldsWithValues: QuestionFieldsWithValues = NotificationUtils.createSoundQuestionFields();

onBeforeMount(async () => {
  await Promise.all([
    samplePresenter.edit(sampleQuestionFields, []),
    soundPresenter.edit(soundQuestionFieldsWithValues.questionFields, soundQuestionFieldsWithValues.defaultValues)
  ]);
  let list: DTOVueGUITheme[] | undefined = await useThemeStore().fetchListThemes();
  if (list)
    themeList.value = list;
})

watch(
  () => [
    soundPresenter.fetchRecord()?.notificationsSound,
    soundPresenter.fetchRecord()?.approvalsSound,
    soundPresenter.fetchRecord()?.messagesSound,
    soundPresenter.fetchRecord()?.taskEndedSound,
  ],
  ([notifications, approvals, messages, taskEnded]) => {
    const store = useNamaFlagsStore();
    if (notifications && notifications !== store.notificationsSound)
      store.updateNotificationsSound(notifications);
    if (approvals && approvals !== store.approvalsSound)
      store.updateApprovalsSound(approvals);
    if (messages && messages !== store.messagesSound)
      store.updateMessagesSound(messages);
    if (taskEnded && taskEnded !== store.taskEndedSound)
      store.updateTaskEndedSound(taskEnded);
  }
)

async function applyTheme(code: string | undefined) {
  await useThemeStore().fetchAndApplyTheme(code);
  currentTheme.value = localStorage.getItem('theme-code');
}

const fieldsPreferences = ref<UserMenuItem[]>([
  {
    vModel: horizontalMode,
    titleId: "horizontalMode",
    helper: Translator.getName("إظهار التسمية في صف أفقي مع الحقل", "Show label with field in same row"),
    defaultIcon: "nm-horizontal"
  },
  {
    vModel: quadLayout,
    titleId: "quadLayout",
    helper: Translator.getName("إظهار أربعة حقول في صف أفقي واحد", "Show four fields in same row"),
    defaultIcon: "nm-quad-layout"
  },
  {
    vModel: showToolbarLabels,
    titleId: "toolbarLabels",
    helper: Translator.getName("إظهار العناوين أسفل كل أيقونة في شريط الأدوات", "Show titles bottom icons of toolbar"),
    defaultIcon: "visibility"
  }
]);

const extraPreferences = ref<UserMenuItem[]>([
  {
    titleId: "densityMode",
    helper: Translator.getName("التحكم بمسافات عناصر الواجهة", "Control spaces of UI"),
    vModelStr: densityMode,
    defaultIcon: "nm-arrow-fit-height",
    hasDivider: true
  }, {
    titleId: "darkMode",
    helper: Translator.getName("تفعيل المظهر الداكن للواجهة", "Enable dark mode of UI"),
    vModel: darkMode,
    defaultIcon: "nm-dark-mode",
    hasDivider: true
  },
  {
    titleId: "showMenuIconColors",
    vModel: showSidebarMenuIconColors,
    defaultIcon: "nm-color-palette",
    hasDivider: true
  }
]);

const densityOptions: QSelectOption[] = [
  {label: Translator.translateId('density.normal'), value: 'Normal'},
  {label: Translator.translateId('Compact'), value: 'Compact'},
  {label: Translator.translateId('Ultra'), value: 'Ultra'}
];

function MenuItemHandler(item: UserMenuItem) {
  if (item.hasOwnProperty('vModel')) {
    item.vModel = !item.vModel
  } else if (item.handler) {
    item.handler();
  }
}

function themeHandler(item: UserMenuItem | undefined) {
  if (item && item.handler)
    item.handler()
}

</script>

<template>
  <div class="tw-mx-auto tw-w-full tw-max-w-6xl tw-space-y-6 tw-px-6">
    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle title="generalPreferences" icon="nm-adjustments"/>
      <div class="tw-divide-y tw-divide-gray-200 dark:tw-divide-gray-700">
        <SettingsRow v-for="item in extraPreferences" :key="item.titleId"
                     @click.capture="MenuItemHandler(item)"
                     @keydown.enter.prevent="MenuItemHandler(item)"
                     @keydown.space.prevent="MenuItemHandler(item)">
          <template #icon>
            <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
          </template>
          <template #label>{{ Translator.translateId(item.titleId) }}</template>
          <template #helper>{{ Translator.translateId(item.helper) }}</template>
          <template #control>
            <div v-if="item.hasOwnProperty('vModelStr')" role="radiogroup"
                 class="tw-inline-flex tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-700">
              <button v-for="(opt, i) in densityOptions" :key="opt.value"
                      type="button" role="radio" :aria-checked="densityMode === opt.value"
                      @click.stop="densityMode = opt.value"
                      v-text="opt.label "
                      :class="[
                        'tw-px-3 tw-py-1 tw-text-xs tw-transition-colors',
                        i > 0 ? 'tw-border-s tw-border-gray-200 dark:tw-border-gray-700' : '',
                        densityMode === opt.value
                          ? 'tw-bg-[rgb(var(--nama-primary-light)/0.12)] tw-font-medium tw-text-primary-light dark:tw-bg-[rgb(var(--nama-primary-dark)/0.2)] dark:tw-text-primary-dark'
                          : 'tw-text-namaFontColor-input-light hover:tw-bg-gray-100 dark:tw-text-namaFontColor-input-dark dark:hover:tw-bg-white/5'
                      ]"/>
              </div>
            <q-toggle :id="item.titleId" v-else-if="item.hasOwnProperty('vModel')" color="primary"
                      :model-value="item.vModel" dense/>
          </template>
        </SettingsRow>
      </div>
    </section>

    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle title="screensPreferences" icon="nm-screen-modifer"/>
      <div class="tw-divide-y tw-divide-gray-200 dark:tw-divide-gray-700">
        <SettingsRow v-for="item in fieldsPreferences" :key="item.titleId"
                     @click.capture="MenuItemHandler(item)"
                     @keydown.enter.prevent="MenuItemHandler(item)"
                     @keydown.space.prevent="MenuItemHandler(item)">
          <template #icon>
            <NamaIcon :default-icon="item.defaultIcon" class="tw-size-5"/>
          </template>
          <template #label>{{ Translator.translateId(item.titleId) }}</template>
          <template #helper>{{ Translator.translateId(item.helper) }}</template>
          <template #control>
            <q-toggle :id="item.titleId" v-if="item.hasOwnProperty('vModel')" color="primary"
                      :model-value="item.vModel" dense/>
          </template>
        </SettingsRow>
      </div>
    </section>
    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle v-if="!isUltra || Screen.lt.sm" title="screenPreview" icon="nm-eye-check"/>
      <div class="tw-w-full">
        <div
          class="tw-rounded-t-lg tw-border tw-border-b-0 tw-border-gray-200 tw-bg-namaBackground-dim-light
                 tw-py-1.5 compact:tw-py-1 ultra:tw-py-0 tw-px-2.5 compact:tw-px-1.5 ultra:tw-px-0.5
                 dark:tw-border-gray-700 dark:tw-bg-namaBackground-dim-dark">
          <div class="tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-2">
            <PageTitle v-if="isUltra && !Screen.lt.sm" title="screenPreview" icon="nm-eye-check"
                       class="!tw-mb-0 !tw-ps-1 tw-max-w-[50%] tw-shrink-0 tw-overflow-x-auto tw-whitespace-nowrap"/>
            <div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-gap-2 tw-overflow-x-auto ultra:tw-place-content-end">
              <a
                class="tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1 tw-rounded-lg tw-p-2 ultra:tw-p-1
                       hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
                :title="Translator.translateId('add')">
                <AddRecordIcon class="tw-h-6 ultra:tw-h-5"/>
                <span
                  v-text="Translator.translateId('add')"
                  class="tw-select-none tw-whitespace-nowrap tw-text-xs tw-capitalize tw-text-namaFontColor-input-light/70 dark:tw-text-white/70"
                  v-if="showToolbarLabels && !isUltra"/>
              </a>
              <a
                class="tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1 tw-rounded-lg tw-p-2 ultra:tw-p-1
                       hover:tw-bg-gray-200 active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600"
                :title="Translator.translateId('delete')">
                <NamaIcon default-icon="nm-delete" class="tw-size-6 ultra:tw-size-5 active-primary"/>
                <span
                  v-text="Translator.translateId('delete')"
                  class="tw-select-none tw-whitespace-nowrap tw-text-xs tw-capitalize tw-text-namaFontColor-input-light/70 dark:tw-text-white/70"
                  v-if="showToolbarLabels && !isUltra"/>
              </a>
            </div>
          </div>
        </div>
        <div
          class="tw-h-[290px] tw-overflow-y-auto tw-rounded-b-lg tw-border tw-border-gray-200 tw-p-3
                 tw-text-namaFontColor-input-light dark:tw-border-gray-700 dark:tw-text-namaFontColor-input-dark">
          <QuestionFieldsEditor :presenter="samplePresenter" :singleColumnGroup="false" :doNotApplyThemeLayouts="false"/>
        </div>
      </div>
    </section>

    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle title="soundEffects" icon="audiotrack"/>
      <div
        class="tw-rounded-lg tw-border tw-border-gray-200 tw-p-3 tw-text-namaFontColor-input-light
               dark:tw-border-gray-700 dark:tw-text-namaFontColor-input-dark">
        <QuestionFieldsEditor :presenter="soundPresenter" :singleColumnGroup="false" :doNotApplyThemeLayouts="true"/>
      </div>
    </section>

    <section
      class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-namaBackground-deep-light tw-p-4
             dark:tw-border-gray-700 dark:tw-bg-namaBackground-deep-dark">
      <PageTitle title="GUIThemesList" icon="nm-theme-editor"/>
      <div class="tw-grid tw-grid-cols-4 tw-gap-3 mobile:tw-grid-cols-1">
        <ThemeCard v-for="item in themeMenu" :key="item.theme?.code"
                   :theme="item.theme!"
                   :active="item.theme?.code === currentTheme"
                   @apply="themeHandler(item)"/>
        <div
          tabindex="0"
          @click="ThemeUtils.goToMyGuiTheme()"
          class="tw-flex tw-min-h-[76px] tw-cursor-pointer tw-select-none tw-items-center tw-justify-center tw-gap-2
                 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-p-3 tw-text-namaFontColor-input-light tw-outline-none
                 hover:tw-border-primary-light focus-visible:tw-ring-2 focus-visible:tw-ring-primary-light
                 dark:tw-border-gray-600 dark:tw-text-namaFontColor-input-dark dark:hover:tw-border-primary-dark
                 dark:focus-visible:tw-ring-primary-dark">
          <NamaIcon default-icon="add" class="tw-size-5"/>
          <span class="tw-text-sm" v-text="Translator.translateId('editMyTheme')"/>
        </div>
      </div>
    </section>
  </div>
</template>
